import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useParams, useSearchParams } from 'react-router-dom';
import EventTicketCheckout from '../components/EventTicketCheckout';
import { useEventManager } from '../hooks/useEventManager';
import { getEventTicketOrderStatus } from '../services/event-payments';

function normalizeLookupValue(value: unknown) {
  return String(value ?? '').trim().toLowerCase().replace(/[_\s]+/g, '-');
}

function isAdultModelTicket(category: string, participantType?: string, name?: string, description?: string) {
  const normalizedCategory = normalizeLookupValue(category);
  const normalizedParticipantType = normalizeLookupValue(participantType);
  const normalizedName = normalizeLookupValue(name);
  const normalizedDescription = normalizeLookupValue(description);

  if (normalizedCategory === 'participant' && normalizedParticipantType === 'adult-model') {
    return true;
  }

  return normalizedName.includes('adult-model') || normalizedDescription.includes('adult-model');
}

function isChildModelTicket(category: string, participantType?: string, name?: string, description?: string) {
  const normalizedCategory = normalizeLookupValue(category);
  const normalizedParticipantType = normalizeLookupValue(participantType);
  const normalizedName = normalizeLookupValue(name);
  const normalizedDescription = normalizeLookupValue(description);

  if (normalizedCategory === 'participant' && normalizedParticipantType === 'child-model') {
    return true;
  }

  return normalizedName.includes('child-model') || normalizedDescription.includes('child-model');
}

function isPayableTicket(status: string, price: number | null) {
  return normalizeLookupValue(status) === 'open' && price !== null && price > 0;
}

export default function EventDetail() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { getEventBySlug, isLoading } = useEventManager();
  const event = slug ? getEventBySlug(slug) : undefined;
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');
  const [orderNotice, setOrderNotice] = useState<string>('');
  const checkoutRef = useRef<HTMLDivElement | null>(null);

  const orderId = searchParams.get('order_id');
  const paymentIntentId = searchParams.get('payment_intent');

  useEffect(() => {
    let isActive = true;

    async function loadOrderNotice() {
      const paymentStatus = searchParams.get('payment_status');
      if (paymentStatus !== 'success' || (!orderId && !paymentIntentId)) {
        if (isActive) {
          setOrderNotice('');
        }
        return;
      }

      try {
        const response = await getEventTicketOrderStatus({
          orderId: orderId || undefined,
          paymentIntentId: paymentIntentId || undefined,
        });
        if (!isActive) return;

        const order = response.order;
        if (order.payment_status === 'paid') {
          setOrderNotice('Payment received. Victoria’s Place will follow up with your ticket and event details by email.');
          return;
        }

        if (order.payment_status === 'refunded') {
          setOrderNotice('This ticket order was refunded. Victoria’s Place can help if you have any follow-up questions.');
          return;
        }

        if (order.payment_status === 'processing') {
          setOrderNotice('Payment is processing. Victoria’s Place will confirm your ticket as soon as Stripe completes the charge.');
          return;
        }

        if (order.payment_status === 'failed') {
          setOrderNotice(order.last_error || 'Payment failed. Please try again or contact Victoria’s Place for help.');
          return;
        }

        if (order.payment_status === 'canceled') {
          setOrderNotice('Payment was canceled before the ticket was completed.');
          return;
        }

        setOrderNotice('Your ticket request is recorded and awaiting payment confirmation.');
      } catch (error) {
        if (isActive) {
          setOrderNotice(
            error instanceof Error
              ? error.message
              : 'Your payment was submitted, but the ticket status could not be confirmed yet.',
          );
        }
      }
    }

    void loadOrderNotice();

    return () => {
      isActive = false;
    };
  }, [orderId, paymentIntentId, searchParams]);

  function scrollToCheckout() {
    if (!checkoutRef.current) {
      return;
    }

    checkoutRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  }

  useEffect(() => {
    if (!selectedTicketId || !checkoutRef.current) {
      return;
    }

    scrollToCheckout();
  }, [selectedTicketId]);

  const eventIsOpen = !!event && event.status === 'open' && event.isPublished;
  const isSelectableTicket = (
    status: string,
    price: number | null,
    category: string,
    name: string,
    description: string,
    participantType?: string,
  ) =>
    eventIsOpen &&
    (isPayableTicket(status, price) ||
      isAdultModelTicket(category, participantType, name, description) ||
      isChildModelTicket(category, participantType, name, description));

  const activeTicket =
    event?.tickets.find(
      (ticket) =>
        ticket.id === selectedTicketId &&
        isSelectableTicket(ticket.status, ticket.price, ticket.category, ticket.name, ticket.description, ticket.participantType),
    ) ??
    event?.tickets.find(
      (ticket) =>
        isSelectableTicket(ticket.status, ticket.price, ticket.category, ticket.name, ticket.description, ticket.participantType),
    ) ??
    null;

  useEffect(() => {
    if (selectedTicketId || !activeTicket) {
      return;
    }

    setSelectedTicketId(activeTicket.id);
  }, [activeTicket, selectedTicketId]);

  function handleTicketSelection(ticketId: string) {
    if (ticketId === activeTicket?.id) {
      scrollToCheckout();
      return;
    }

    setSelectedTicketId(ticketId);
  }

  if (isLoading) {
    return (
      <div className="page-shell event-detail-page space-y-8">
        <section className="event-detail-hero">
          <div className="event-detail-copy">
            <span className="eyebrow">Event details</span>
            <h1>Loading event details...</h1>
          </div>
        </section>
      </div>
    );
  }

  if (!event) {
    return <Navigate to="/events" replace />;
  }

  return (
    <div className="page-shell event-detail-page space-y-8">
      <section className="event-detail-hero">
        <div className="event-detail-copy">
          <span className="eyebrow">Event details</span>
          <p className="events-kicker">{event.status.replace('-', ' ')}</p>
          <h1>{event.title}</h1>
          <p className="event-detail-lead">{event.shortDescription}</p>

          <div className="event-detail-meta">
            <div className="event-detail-meta-item">
              <span>Date</span>
              <strong>{event.dateLabel}</strong>
            </div>
            <div className="event-detail-meta-item">
              <span>Time</span>
              <strong>{event.timeLabel}</strong>
            </div>
            <div className="event-detail-meta-item">
              <span>Location</span>
              <strong>{event.locationName}</strong>
              <em className="event-detail-meta-subline">{event.locationAddress || 'Columbia, South Carolina'}</em>
            </div>
          </div>
        </div>

        <figure className="events-photo-card events-photo-card--main">
          <img src={event.heroImage} alt={event.title} />
        </figure>
      </section>

      <section className="event-detail-story">
        <div className="event-detail-story-copy">
          <p className="home-section-label">About this event</p>
          <h2>A welcoming event experience built around connection and support.</h2>
          <p>{event.fullDescription}</p>
          <p>
            Victoria&apos;s Place events are meant to be clear, inviting, and easy to join, so families, guests, and
            community supporters can focus on showing up and being part of the moment.
          </p>
        </div>

        <div className="event-detail-highlight-list">
          {event.highlights.map((highlight) => (
            <article key={highlight} className="event-detail-highlight">
              <p>{highlight}</p>
            </article>
          ))}
        </div>
      </section>

      {event.galleryImages.length > 0 ? (
        <section className="event-detail-gallery">
          <div className="events-section-heading">
            <p className="home-section-label">Event photos</p>
            <h2>A visual look at the event atmosphere and story.</h2>
          </div>

          <div className="event-detail-gallery-grid">
            {event.galleryImages.map((image, index) => (
              <figure
                key={`${image}-${index}`}
                className={`event-detail-gallery-card ${index === 0 ? 'event-detail-gallery-card--cover' : ''}`}
              >
                <img
                  src={image}
                  alt={index === 0 ? `${event.title} cover image` : `${event.title} gallery image ${index}`}
                />
              </figure>
            ))}
          </div>
        </section>
      ) : null}

      <section className="event-detail-tickets">
        <div className="events-section-heading">
          <p className="home-section-label">Ticket types</p>
          <h2>{event.title}</h2>
        </div>

        {orderNotice ? <div className="auth-success">{orderNotice}</div> : null}

        <div className="events-ticket-row">
          {event.tickets.map((ticket) => (
            <article key={ticket.id} className="events-ticket-card">
              <p className="info-kicker">{ticket.category.replace('-', ' ')}</p>
              <h3>{ticket.name}</h3>
              <strong>{ticket.priceLabel}</strong>
              <p>{ticket.description}</p>
              {eventIsOpen && isPayableTicket(ticket.status, ticket.price) ? (
                <button
                  type="button"
                  className={`tap-target ${ticket.id === activeTicket?.id ? 'button-primary' : 'button-secondary'} event-ticket-action`}
                  onClick={() => handleTicketSelection(ticket.id)}
                >
                  {ticket.id === activeTicket?.id ? 'Checkout below' : 'Buy this ticket'}
                </button>
              ) : eventIsOpen && isAdultModelTicket(ticket.category, ticket.participantType, ticket.name, ticket.description) ? (
                <button
                  type="button"
                  className={`tap-target ${ticket.id === activeTicket?.id ? 'button-primary' : 'button-secondary'} event-ticket-action`}
                  onClick={() => handleTicketSelection(ticket.id)}
                >
                  {ticket.id === activeTicket?.id ? 'Application below' : 'Apply as adult model'}
                </button>
              ) : eventIsOpen && isChildModelTicket(ticket.category, ticket.participantType, ticket.name, ticket.description) ? (
                <button
                  type="button"
                  className={`tap-target ${ticket.id === activeTicket?.id ? 'button-primary' : 'button-secondary'} event-ticket-action`}
                  onClick={() => handleTicketSelection(ticket.id)}
                >
                  {ticket.id === activeTicket?.id ? 'Application below' : 'Register child model'}
                </button>
              ) : eventIsOpen ? (
                <div className="event-ticket-status event-ticket-status--coming-soon">Details coming soon</div>
              ) : ticket.category === 'participant' ? (
                <Link to="/contact" className="button-secondary tap-target event-ticket-action">
                  Ask about registration
                </Link>
              ) : (
                <div className="event-ticket-status event-ticket-status--coming-soon">Available when this event opens</div>
              )}
            </article>
          ))}
        </div>

        {activeTicket ? (
          <div ref={checkoutRef} style={{ scrollMarginTop: '8rem' }}>
            <EventTicketCheckout event={event} ticket={activeTicket} />
          </div>
        ) : null}
      </section>

      <section className="events-feature-band">
        <div className="events-feature-copy">
          <p className="home-section-label home-section-label--light">Stay Connected</p>
          <h2>Questions about attending, supporting, or participating?</h2>
          <p>
            Reach out if you want to learn more about this event, vendor opportunities, or the ways Victoria&apos;s Place
            creates space for children and families to be seen, encouraged, and celebrated.
          </p>
        </div>

        <div className="event-detail-actions">
          <Link to="/contact" className="button-primary tap-target">
            Ask about this event
          </Link>
          <Link to="/events" className="button-secondary tap-target">
            Back to events
          </Link>
        </div>
      </section>
    </div>
  );
}
